/**
 * AVBP Project Conversation — Firestore data layer (Phase 1 · Sprint 2).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  VERBATIM-SACROSANCT CONTRACT (owner-locked memory, do not violate)
 * ─────────────────────────────────────────────────────────────────────────
 *  Three collections under each project, with one hard rule:
 *
 *   projects/{pid}/messages/{msgId}    ← CANONICAL. Raw human input
 *                                         (text · attachments · voice +
 *                                         transcript). Append-only.
 *                                         NEVER mutated, NEVER overwritten,
 *                                         NEVER deleted. This is the record.
 *
 *   projects/{pid}/proposals/{propId}  ← DERIVATIVE. AI-extracted intents.
 *                                         Every proposal carries
 *                                         `sourceMessageId` (FK back to the
 *                                         verbatim message it was derived
 *                                         from). A proposal is a *suggestion*
 *                                         — it does NOT touch the project
 *                                         record until a human commits it.
 *
 *   projects/{pid}/ledger/{entryId}    ← IMMUTABLE per-project audit trail.
 *                                         A ledger entry is appended ONLY on
 *                                         commit. Reject writes nothing.
 *                                         (The full Ledger module/UI is a
 *                                         later sprint; the write contract
 *                                         is honoured here.)
 *
 *  Commit  = proposal.status → 'committed'  + append ledger entry
 *            (+ the real record write — see COMMIT SEAM below).
 *  Reject  = proposal.status → 'rejected'   + NOTHING else written.
 *
 *  COMMIT SEAM: the actual write into the project record (intake row,
 *  finding doc, photo-tag bind…) is downstream-sprint territory
 *  (engagementTemplate + per-tier record shapes don't exist yet). For
 *  Sprint 2 the commit is fully modelled — proposal state-machine +
 *  immutable ledger append — and the record-write is an explicit, typed
 *  no-op seam (`applyProposalToRecord`) so the capture→propose→
 *  commit/reject→ledger loop is end-to-end exercisable today and the real
 *  writer drops in here with zero surface change.
 * ─────────────────────────────────────────────────────────────────────────
 */
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { demoSeedEnabled } from './demoSeed';
import type { DraftProposal } from './extractor';
import {
  escalationAnchor,
  getEngagementTemplate,
  type EngagementTemplate,
  type Gate,
  type PaymentModePolicy,
} from './engagementTemplate';
import {
  ENGAGEMENT_TEMPLATE_VERSION,
  applyGateClearance,
  assertStageEntry,
  emptyGateState,
  type ProjectGateState,
  type RecommendationEvent,
} from './projectLedger';

/* ─────────────── Verbatim message (canonical) ─────────────── */

export type AttachmentKind = 'photo' | 'video' | 'audio' | 'pdf' | 'dwg';

export interface Attachment {
  kind: AttachmentKind;
  /**
   * Firebase Storage download URL. Empty string on a deliverable_event chip:
   * the append-only record stores no signed URL (it would expire); Slice 3
   * mints a signed URL on demand from `storagePath` via a CF.
   */
  url: string;
  name?: string;
  /**
   * Phase-2 Slice 2 — a delivered-report PDF chip carries the FK to its
   * durable artefact + the raw Storage path so Slice 3's viewer can resolve a
   * signed download URL. Optional: ordinary chat attachments don't set these.
   */
  artefactId?: string | null;
  storagePath?: string | null;
  /**
   * Phase-2 Slice 3 — the report's tier + canonical runId, carried on the chip
   * so the client viewer can call getReportDownloadUrl({clientId, projectId,
   * tier, runId}) to mint a signed download URL. clientId + projectId come from
   * the conversation context (project meta + route pid). Optional: only set on
   * a delivered-report chip.
   */
  tier?: string | null;
  runId?: string | null;
}

export interface VoiceNote {
  url: string;
  /** Seconds. */
  durationSec: number;
  /** OpenAI Whisper transcript (engine locked in memory). Stored verbatim. */
  transcript: string;
}

/**
 * THE SPINE — owner-locked conversation visibility model (2026-05-17).
 *
 * ONE conversation per project = the single source of truth, a normal
 * scroll-up chat history SHARED by client AND team. A message carries an
 * authorRole + an optional `kind`:
 *
 *  - kind 'chat'      → a human message (client or team). Always visible to
 *                       both client and team (subject to `internal`).
 *  - kind 'milestone' → a transactional/milestone EVENT posted INTO the
 *                       same thread as a visible chat item. The client sees
 *                       these three milestone subtypes: 'quote_generated',
 *                       'report_sent', 'payment_made'. Team sees all.
 *
 * `internal: true` → a team/admin-only message (internal note). NEVER
 * client-visible. The AI extraction / drafts / Review Queue layer lives in
 * the separate `proposals` collection (already team-only) — it is layered
 * ON the same thread for team+admin and is structurally never in the
 * client's message stream.
 *
 * Client verbatim is preserved (append-only); AI is derivative. Milestone
 * events are system-authored, immutable, append-only — same discipline.
 */
export type MessageKind = 'chat' | 'milestone';

export type AuthorRole =
  | 'client'
  | 'staff'
  | 'admin'
  | 'system'
  | 'external_professional';

/**
 * The three client-visible milestone subtypes (owner-locked spine). Any
 * other milestone subtype is treated as team-only.
 */
export type MilestoneSubtype =
  | 'quote_generated'
  | 'report_sent'
  | 'payment_made'
  /* team-only milestone subtypes (never client-rendered) */
  | 'gate_cleared'
  | 'stage_changed'
  | 'internal';

/** Milestone subtypes the CLIENT sees inside the shared thread. */
export const CLIENT_VISIBLE_MILESTONES: MilestoneSubtype[] = [
  'quote_generated',
  'report_sent',
  'payment_made',
];

export interface ConvMessage {
  id: string;
  /** Display name of the human/system author (participant). */
  authorName: string;
  authorUid: string;
  /** Participant role of the author. Absent on legacy rows → inferred. */
  authorRole?: AuthorRole;
  /** 'chat' (human) or 'milestone' (transactional event). Absent → 'chat'. */
  kind?: MessageKind;
  /** Only on kind 'milestone' — which milestone this is. */
  milestone?: MilestoneSubtype;
  /**
   * Team/admin-only message (internal note). NEVER client-visible. Absent
   * or false → shared. Mirrors the existing legacy `internal` rule.
   */
  internal?: boolean;
  /** The raw text exactly as typed. Empty string if attachment/voice-only. */
  body: string;
  attachments: Attachment[];
  voice: VoiceNote | null;
  createdAt: Timestamp | null;
  /** Client-side ms fallback for ordering before serverTimestamp resolves. */
  localTs: number;
}

/* ─────────────── Spine: visibility resolution (PURE) ─────────────── */

/** Legacy/absent rows: no `kind` → 'chat'. */
export function messageKindOf(m: { kind?: MessageKind }): MessageKind {
  return m.kind ?? 'chat';
}

/**
 * Is this message visible to a CLIENT-track viewer? Owner-locked spine:
 *  - internal notes      → NEVER
 *  - chat                → yes (client's own + team replies, when not internal)
 *  - milestone           → only the 3 client-visible subtypes
 * Team/admin see everything (the AI/review layer is the separate proposals
 * collection, which is structurally never in this stream).
 *
 * Pure + dependency-free so the Node test runner exercises it directly.
 */
export function isClientVisible(m: {
  kind?: MessageKind;
  milestone?: MilestoneSubtype;
  internal?: boolean;
}): boolean {
  if (m.internal === true) return false;
  if (messageKindOf(m) === 'milestone') {
    return (
      m.milestone != null &&
      (CLIENT_VISIBLE_MILESTONES as string[]).includes(m.milestone)
    );
  }
  return true; // shared human chat
}

/**
 * Filter a raw thread to what a given login role may see. Admin + staff
 * see the full thread (the review/AI layer is separate); client + every
 * other role get the spine-filtered view. This is THE single function the
 * Conversation screen calls — the visibility split is enforced here, once.
 */
export function visibleThread(
  msgs: ConvMessage[],
  opts: { isTeamView: boolean },
): ConvMessage[] {
  if (opts.isTeamView) return msgs;
  return msgs.filter(isClientVisible);
}

/* ─────────────── Proposal (AI-derived, FK to message) ─────────────── */

export type ProposalKind =
  | 'create_finding'
  | 'update_intake'
  | 'photo_tag'
  | 'add_participant';

export type ProposalStatus = 'open' | 'committed' | 'rejected';

export interface Proposal {
  id: string;
  kind: ProposalKind;
  /** FK back to the verbatim source — the derivative-with-FK contract. */
  sourceMessageId: string;
  /** One-line tag shown on the card (e.g. "toilet_in_SE"). */
  title: string;
  /** Human-readable card body. */
  detail: string;
  /** Optional severity word ("needs remedy" / "repair"). */
  severity?: string;
  /** Structured payload the real record-writer will consume downstream. */
  payload: Record<string, unknown>;
  /** Source-jump breadcrumb ("from Khilendra · 14:22 · 📎 3 photos"). */
  sourceLabel: string;
  status: ProposalStatus;
  /**
   * 24-business-hour auto-escalation anchor. When set + still `open`, the
   * card shows the escalation countdown. (Escalation *action* is a later
   * sprint; the indicator + data anchor are honoured here.)
   */
  escalateAt: Timestamp | null;
  /**
   * Escalation bubble vs ambient chip. `true` → AI is ASKING the human
   * something (e.g. add a participant) and must render as a bordered
   * bubble with actions. `false`/absent → ambient dashed chip. This is the
   * "chip by default, bubble only when asking" lock, encoded in data.
   */
  asksUser?: boolean;
  createdAt: Timestamp | null;
}

/* ─────────────── Ledger (immutable audit) ─────────────── */

/**
 * IMMUTABLE per-project audit entry. Sprint 4 widened this from
 * commit-only to the canonical record of EVERY consequential event.
 * Sprint-2 fields (proposalId/kind) stay so existing rows + listeners
 * keep working; `eventType` + the event-specific fields are optional and
 * absent rows are treated as 'proposal_commit' (see projectLedger
 * `eventTypeOf`). APPEND-ONLY — no update/delete export exists anywhere.
 */
export interface LedgerEntry {
  id: string;
  /** Sprint-4 discriminator. Absent on legacy Sprint-2 rows. */
  eventType?:
    | 'proposal_commit'
    | 'recommendation'
    | 'decision'
    | 'gate_clearance'
    | 'template_version_lock'
    | 'stage_transition';
  /** Sprint-2 commit fields (present only on proposal_commit rows). */
  proposalId?: string;
  sourceMessageId?: string;
  kind?: ProposalKind;
  summary: string;
  /** Who committed / acted. */
  actorUid: string;
  actorName: string;
  at: Timestamp | null;
  /* — recommendation event (auto-recall source) — */
  topicKey?: string;
  recommendation?: string;
  /* — gate_clearance event — */
  gateId?: string;
  gateKind?: Gate['kind'];
  blocksStageKey?: string;
  /* — template_version_lock event — */
  templateVersion?: string;
  tier?: string;
  /* — stage_transition event — */
  fromStageKey?: string | null;
  toStageKey?: string;
}

/* ─────────────── Project meta (lightweight, Sprint-2 demo) ─────────────── */

export interface ProjectMeta {
  id: string;
  vp: string;
  /**
   * The project's own human-readable name (2026-05-22) — distinct from the
   * client name and the `VP###` code. Optional: a legacy/demo project doc
   * predates the field. `getProjectMeta` spreads the whole doc, so when the
   * project doc carries `projectName` it lands here automatically; render
   * `projectLabel(...)` for a graceful placeholder when it is absent.
   */
  projectName?: string;
  /** Display CACHE only — the canonical client name lives on the
   *  clients/{id} master; resolve it via `useClientNames`. */
  clientName: string;
  clientNo: string;
  /** IA-fix FK to clients/{id} — source of truth for the client identity.
   *  Optional: a legacy/demo project doc may pre-date the IA fix. */
  clientId?: string;
  tier: string;
  stage: string;
  /** Sprint-4: persisted per-project gate state (money/curation/
   *  consultation). Drives real money-before-delivery enforcement.
   *  Absent on legacy/demo docs → treated as an empty gate state. */
  gateState?: ProjectGateState;
  /** Sprint-4: the engagementTemplate version this project is LOCKED to,
   *  stamped at creation. Absent on legacy/demo docs → shell falls back
   *  to current and says so (resolveTemplateVersion). */
  templateVersion?: string;
  /** Sprint-4: current stage key for real gate enforcement (replaces the
   *  Sprint-3 demo "index 1" stand-in once a project drives it). */
  activeStageKey?: string;
}

/* ════════════════════ Paths ════════════════════ */

const messagesCol = (pid: string) => collection(db, 'projects', pid, 'messages');
const proposalsCol = (pid: string) => collection(db, 'projects', pid, 'proposals');
const ledgerCol = (pid: string) => collection(db, 'projects', pid, 'ledger');

/* ════════════════════ Realtime listeners ════════════════════ */

export function listenMessages(
  pid: string,
  cb: (msgs: ConvMessage[]) => void,
  onErr?: (e: Error) => void,
  clientOnly = false,
): Unsubscribe {
  // Client sessions (portal visibility matrix v1, AD-010) see only messages
  // explicitly marked clientVisible=true. Staff sessions see everything.
  // Ordering stays on localTs (the existing per-doc client clock) so the only
  // new index needed is clientVisible+localTs.
  const q = clientOnly
    ? query(
        messagesCol(pid),
        where('clientVisible', '==', true),
        orderBy('localTs', 'asc'),
      )
    : query(messagesCol(pid), orderBy('localTs', 'asc'));
  return onSnapshot(
    q,
    (snap) => {
      cb(
        snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ConvMessage, 'id'>) })),
      );
    },
    (e) => onErr?.(e),
  );
}

export function listenProposals(
  pid: string,
  cb: (props: Proposal[]) => void,
  onErr?: (e: Error) => void,
): Unsubscribe {
  const q = query(proposalsCol(pid), orderBy('createdAt', 'asc'));
  return onSnapshot(
    q,
    (snap) => {
      cb(
        snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Proposal, 'id'>) })),
      );
    },
    (e) => onErr?.(e),
  );
}

export function listenLedger(
  pid: string,
  cb: (entries: LedgerEntry[]) => void,
  onErr?: (e: Error) => void,
): Unsubscribe {
  const q = query(ledgerCol(pid), orderBy('at', 'desc'));
  return onSnapshot(
    q,
    (snap) => {
      cb(
        snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<LedgerEntry, 'id'>) })),
      );
    },
    (e) => onErr?.(e),
  );
}

/* ════════════════════ Writes ════════════════════ */

export interface NewMessageInput {
  authorName: string;
  authorUid: string;
  body: string;
  attachments?: Attachment[];
  voice?: VoiceNote | null;
  contactId?: string;   // enables contact-level thread view (M3.1)
}

/**
 * Append a verbatim message. APPEND-ONLY by construction — there is
 * deliberately no `updateMessage`/`deleteMessage` export. Returns the
 * new message id so the extraction seam can FK to it.
 */
export async function postMessage(
  pid: string,
  input: NewMessageInput & { authorRole?: AuthorRole; internal?: boolean },
): Promise<string> {
  const ref = await addDoc(messagesCol(pid), {
    authorName: input.authorName,
    authorUid: input.authorUid,
    authorRole: input.authorRole ?? 'staff',
    kind: 'chat' as MessageKind,
    internal: input.internal === true,
    body: input.body,
    attachments: input.attachments ?? [],
    voice: input.voice ?? null,
    projectId: pid,
    contactId: input.contactId ?? null,
    channel: 'portal' as const,
    direction: 'out' as const,
    clientVisible: false,
    createdAt: serverTimestamp(),
    localTs: Date.now(),
  });
  return ref.id;
}

/**
 * Post a team/admin-only INTERNAL note into the same thread. Structurally
 * a chat message with `internal:true` — the spine filter (isClientVisible)
 * guarantees it never reaches a client viewer. APPEND-ONLY like every
 * message. (Firestore rules also enforce client invisibility server-side —
 * the existing `internal != true` rule already covers this path.)
 */
export async function postInternalNote(
  pid: string,
  input: NewMessageInput & { authorRole?: AuthorRole },
): Promise<string> {
  return postMessage(pid, { ...input, internal: true });
}

/**
 * THE SPINE write — post a MILESTONE event INTO the shared thread as a
 * visible chat item. The 3 client-visible subtypes (quote_generated ·
 * report_sent · payment_made) appear in the client's stream; team-only
 * subtypes (gate_cleared · stage_changed · internal) are filtered for
 * clients by `isClientVisible`. System-authored, append-only, immutable
 * (no update/delete export) — same verbatim discipline as a message.
 *
 * Note: this records the THREAD-VISIBLE event. The immutable per-project
 * Ledger (clearGate / transitionStage / recordRecommendation) is the
 * separate audit trail and is unchanged — a milestone post is the
 * client-facing reflection of a consequential event, not its system of
 * record.
 */
export async function postMilestone(
  pid: string,
  subtype: MilestoneSubtype,
  body: string,
  actor: { uid: string; name: string } = { uid: 'system', name: 'AVBP' },
): Promise<string> {
  const ref = await addDoc(messagesCol(pid), {
    authorName: actor.name,
    authorUid: actor.uid,
    authorRole: 'system' as AuthorRole,
    kind: 'milestone' as MessageKind,
    milestone: subtype,
    internal: !(CLIENT_VISIBLE_MILESTONES as string[]).includes(subtype),
    body,
    attachments: [],
    voice: null,
    channel: 'portal' as const,
    direction: 'out' as const,
    clientVisible: false,
    createdAt: serverTimestamp(),
    localTs: Date.now(),
  });
  return ref.id;
}

/**
 * Persist AI-derived proposals from extractor drafts. Always FK'd to a
 * real message id. This is the single place where the 24-business-hour
 * `escalateAt` Timestamp is minted — extraction stays pure (it only flags
 * `wantsEscalation`), persistence stamps the real Firestore Timestamp.
 */
export async function postProposalsFromDrafts(
  pid: string,
  drafts: DraftProposal[],
  /**
   * The engagement template for this project. Sprint 3: escalation is
   * now working-calendar resolved (business hours · weekly-off · holiday
   * aware) and the per-tier `escalationBusinessHours` budget drives it.
   * Falls back to L2B (the Sprint-2 reference tier) if absent.
   */
  tpl?: EngagementTemplate | null,
): Promise<void> {
  if (!drafts.length) return;
  // Sprint 3: the naïve `now + 24h` wall-clock is replaced by the
  // working-calendar resolver. `escalationAnchor` skips weekends,
  // holidays and after-hours so the SLA countdown is real.
  const template = tpl ?? getEngagementTemplate('L2B')!;
  const esc = Timestamp.fromMillis(escalationAnchor(template).getTime());
  const batch = writeBatch(db);
  for (const d of drafts) {
    const { wantsEscalation, ...rest } = d;
    const ref = doc(proposalsCol(pid));
    batch.set(ref, {
      ...rest,
      escalateAt: wantsEscalation ? esc : null,
      status: 'open' as ProposalStatus,
      createdAt: serverTimestamp(),
    });
  }
  await batch.commit();
}

/**
 * COMMIT SEAM — the real write into the project record.
 *
 * Downstream-sprint territory: the per-tier record shapes (intake rows,
 * finding docs, photo-tag binds) are defined by the engagementTemplate
 * engine, which does not exist yet. This is the single, typed place where
 * that writer drops in. For Sprint 2 it is an intentional no-op so the
 * full loop demos without fabricating a record schema we'd have to redo.
 */
async function applyProposalToRecord(
  _pid: string,
  _p: Proposal,
): Promise<void> {
  // SEAM (Sprint 2 no-op). Real per-kind record write lands with the
  // engagementTemplate engine (Phase 1 · step 4). Do NOT inline record
  // shapes here before that lock — keep this the only insertion point.
  return;
}

/**
 * Commit a proposal:
 *   1. real record write (seam, see above)
 *   2. proposal.status → 'committed'
 *   3. append an IMMUTABLE ledger entry
 * Steps 2+3 are one atomic batch. Reject writes NOTHING but the status.
 */
export async function commitProposal(
  pid: string,
  p: Proposal,
  actor: { uid: string; name: string },
): Promise<void> {
  await applyProposalToRecord(pid, p);

  const batch = writeBatch(db);
  batch.update(doc(proposalsCol(pid), p.id), { status: 'committed' });
  const ledgerRef = doc(ledgerCol(pid));
  batch.set(ledgerRef, {
    eventType: 'proposal_commit',
    proposalId: p.id,
    sourceMessageId: p.sourceMessageId,
    kind: p.kind,
    summary: `${p.kind} · ${p.title} — ${p.detail}`,
    actorUid: actor.uid,
    actorName: actor.name,
    at: serverTimestamp(),
  });
  await batch.commit();
}

/** Reject: status only. Nothing is written to the record or the ledger. */
export async function rejectProposal(
  pid: string,
  proposalId: string,
): Promise<void> {
  await updateDoc(doc(proposalsCol(pid), proposalId), {
    status: 'rejected' as ProposalStatus,
  });
}

/* ════════════════════ Queue-depth pip ════════════════════ */

export type QueueColour = 'g' | 'a' | 'r';

/** Locked thresholds: green <4 · amber 4–6 · red >6. */
export function queueColour(openCount: number): QueueColour {
  if (openCount > 6) return 'r';
  if (openCount >= 4) return 'a';
  return 'g';
}

/* ════════════════════ Demo project (Sprint-2 review fixture) ════════════════════ */

/**
 * Stable demo project id so the reviewer always lands on a populated
 * conversation. Real project creation/selection is the engagementTemplate
 * sprint — this is purely a Sprint-2 review fixture, isolated to one id.
 */
export const DEMO_PID = 'VP656';

const DEMO_META: ProjectMeta = {
  id: DEMO_PID,
  vp: 'VP656',
  clientName: 'Sitaramji Agarwal',
  clientNo: 'AV-C-0560',
  tier: 'L2B · Detailed Assessment',
  stage: 'Intake',
};

/**
 * #992: a neutral meta for an unknown project id when the demo seed is
 * OFF — never the fabricated "Sitaramji Agarwal / VP656" identity.
 */
function neutralMeta(pid: string): ProjectMeta {
  return {
    id: pid,
    vp: pid,
    clientName: '—',
    clientNo: '—',
    tier: '—',
    stage: '—',
  };
}

export async function getProjectMeta(pid: string): Promise<ProjectMeta> {
  const snap = await getDoc(doc(db, 'projects', pid));
  const base = demoSeedEnabled() ? DEMO_META : neutralMeta(pid);
  if (snap.exists()) {
    const d = snap.data() as Partial<ProjectMeta>;
    return { ...base, ...d, id: pid };
  }
  return base;
}

/**
 * Idempotently ensure the demo project document exists. The conversation
 * itself starts EMPTY on purpose — the reviewer drives the
 * capture→propose→commit loop by sending messages (the brief's click-path).
 * Re-running never duplicates: it only creates the project meta doc if
 * absent and never touches messages/proposals/ledger.
 */
export async function ensureDemoProject(): Promise<ProjectMeta> {
  // #992: with the demo seed OFF, NEVER write the VP656 fixture into the
  // live data. Bare /conversation is a neutral empty state instead.
  if (!demoSeedEnabled()) return neutralMeta(DEMO_PID);
  const ref = doc(db, 'projects', DEMO_PID);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    // Sprint-4: a project LOCKS its engagementTemplate version at
    // creation (memory project_avbp_iterative_streamline_strategy) +
    // starts with an empty gate state. The version-lock is also an
    // immutable ledger entry — project creation is a consequential event.
    await setDoc(ref, {
      vp: DEMO_META.vp,
      clientName: DEMO_META.clientName,
      clientNo: DEMO_META.clientNo,
      tier: DEMO_META.tier,
      stage: DEMO_META.stage,
      templateVersion: ENGAGEMENT_TEMPLATE_VERSION,
      gateState: emptyGateState(),
      createdAt: serverTimestamp(),
      seededBy: 'sprint2-demo-fixture',
    });
    await addDoc(ledgerCol(DEMO_PID), {
      eventType: 'template_version_lock',
      tier: DEMO_META.tier,
      templateVersion: ENGAGEMENT_TEMPLATE_VERSION,
      summary: `Project created · engagementTemplate locked to ${ENGAGEMENT_TEMPLATE_VERSION} (${DEMO_META.tier}). Streamline edits apply only to new engagements.`,
      actorUid: 'system',
      actorName: 'AVBP',
      at: serverTimestamp(),
    });
  }
  return { ...DEMO_META, ...(snap.data() as Partial<ProjectMeta>) };
}

/* ════════════════════ Sprint-4 · gate state + recall writes ════════════════════ */

/**
 * Clear a gate on a project. ATOMIC: (1) replace the persisted
 * `gateState` on the project doc with the reduced state, (2) append an
 * IMMUTABLE `gate_clearance` ledger entry. A clearance is a consequential
 * event — it is recorded forever. Mirrors `commitProposal`'s one-batch
 * discipline. NOTE: this records gate STATE only — it does NOT integrate
 * a payment provider (Razorpay is a later sprint, per the brief).
 */
export async function clearGate(
  pid: string,
  gate: Gate,
  prev: ProjectGateState,
  actor: { uid: string; name: string },
): Promise<ProjectGateState> {
  const next = applyGateClearance(prev, gate);
  const batch = writeBatch(db);
  batch.update(doc(db, 'projects', pid), { gateState: next });
  batch.set(doc(ledgerCol(pid)), {
    eventType: 'gate_clearance',
    gateId: gate.kind === 'curation' ? 'curation' : gate.id,
    gateKind: gate.kind,
    blocksStageKey: gate.blocksStageKey,
    summary: `Gate cleared · ${
      'label' in gate ? gate.label : gate.kind
    } (was blocking "${gate.blocksStageKey}")`,
    actorUid: actor.uid,
    actorName: actor.name,
    at: serverTimestamp(),
  });
  await batch.commit();
  // THE SPINE: a money gate clearance is a "payment made" event the client
  // sees in the shared thread; non-money gate clearances post a team-only
  // milestone (filtered from clients by isClientVisible). The ledger append
  // above is the immutable audit; this is its thread-visible reflection.
  if (gate.kind === 'money') {
    await postMilestone(
      pid,
      'payment_made',
      `Payment received · ${gate.label}`,
      actor,
    );
  } else {
    await postMilestone(
      pid,
      'gate_cleared',
      `Gate cleared · ${'label' in gate ? gate.label : gate.kind}`,
      actor,
    );
  }
  return next;
}

/**
 * Enforced stage transition. Throws StageBlockedError if the persisted
 * gate state does not clear the target stage (real money-before-delivery
 * enforcement — a blocked stage is genuinely unenterable). For
 * delivery-guarding stages the Sprint-3 server-recheck seam
 * (`assertServerSideGate`) is invoked on the real path. On success it
 * persists the new active stage + appends an immutable stage_transition
 * ledger entry.
 */
/**
 * THE real server-recheck — implements the Sprint-3 `assertServerSideGate`
 * seam by round-tripping to the `recheckGate` Cloud Function (DB task
 * #877). The server re-reads `projects/{pid}.gateState` + tier
 * authoritatively, re-runs canEnterStage, and enforces curation =
 * assignee|admin (no tier-fallback). The client verdict is NEVER trusted
 * for delivery: the server's verdict wins. A transport failure is treated
 * as a BLOCK (fail-safe — a delivery write must not proceed on an
 * unverified gate).
 */
export function makeServerGateRecheck(pid: string) {
  const fn = httpsCallable<
    { projectId: string; stageKey: string },
    { allowed: boolean; blockedBy: string[]; serverChecked: boolean }
  >(functions, 'recheckGate');
  return async (
    _tpl: EngagementTemplate,
    stageKey: string,
    clientVerdict: { allowed: boolean; blockedBy: Gate[] },
  ): Promise<{ allowed: boolean; blockedBy: Gate[] }> => {
    try {
      const res = await fn({ projectId: pid, stageKey });
      const d = res.data;
      if (!d.serverChecked || !d.allowed) {
        return { allowed: false, blockedBy: clientVerdict.blockedBy };
      }
      return { allowed: true, blockedBy: [] };
    } catch {
      // Fail-safe: an unverifiable gate must NOT release a delivery.
      return { allowed: false, blockedBy: clientVerdict.blockedBy };
    }
  };
}

export async function transitionStage(
  pid: string,
  tpl: EngagementTemplate,
  fromStageKey: string | null,
  toStageKey: string,
  state: ProjectGateState,
  actor: { uid: string; name: string },
  // owner directive 2026-05-19 — configurable money policy. Default keeps
  // the locked money-before-delivery hard block. The caller resolves the
  // project's effective mode (resolvePaymentMode) and passes it; under
  // 'work_first' the money gate no longer blocks the transition (still
  // tracked + shown). Curation/consultation gates always block.
  paymentMode: PaymentModePolicy = 'payment_before',
): Promise<void> {
  await assertStageEntry(
    tpl,
    toStageKey,
    state,
    makeServerGateRecheck(pid),
    paymentMode,
  );
  const batch = writeBatch(db);
  batch.update(doc(db, 'projects', pid), { activeStageKey: toStageKey });
  batch.set(doc(ledgerCol(pid)), {
    eventType: 'stage_transition',
    fromStageKey: fromStageKey,
    toStageKey,
    summary: `Stage → ${toStageKey}${
      fromStageKey ? ` (from ${fromStageKey})` : ''
    }`,
    actorUid: actor.uid,
    actorName: actor.name,
    at: serverTimestamp(),
  });
  await batch.commit();
  // THE SPINE: stage transitions are a team-only milestone in the thread
  // (filtered from clients). The client's journey/status surface reads the
  // ledger stage_transition events; this keeps the team thread coherent.
  await postMilestone(
    pid,
    'stage_changed',
    `Stage → ${toStageKey}${fromStageKey ? ` (from ${fromStageKey})` : ''}`,
    actor,
  );
}

/**
 * THE SPINE: record that a QUOTE was generated / a REPORT was sent. Both
 * post a client-visible milestone into the shared thread (owner-locked:
 * the client sees a chat entry whenever a quote is generated and whenever
 * a report is sent/delivered). Thin wrappers so callers state intent.
 */
export async function postQuoteGeneratedMilestone(
  pid: string,
  label: string,
  actor: { uid: string; name: string } = { uid: 'system', name: 'AVBP' },
): Promise<string> {
  return postMilestone(pid, 'quote_generated', `Quote generated · ${label}`, actor);
}

export async function postReportSentMilestone(
  pid: string,
  label: string,
  actor: { uid: string; name: string } = { uid: 'system', name: 'AVBP' },
): Promise<string> {
  return postMilestone(pid, 'report_sent', `Report delivered · ${label}`, actor);
}

/**
 * Record a recommendation as an immutable ledger event with a
 * deterministic `topicKey` (auto-recall source — memory
 * feedback_avbp_recommendation_memory). The topicKey is derived via the
 * single TopicMatcher seam so a later LLM-similarity matcher is a
 * one-line swap.
 */
export async function recordRecommendation(
  pid: string,
  topicKey: string,
  recommendation: string,
  actor: { uid: string; name: string },
  sourceMessageId?: string,
): Promise<void> {
  await addDoc(ledgerCol(pid), {
    eventType: 'recommendation',
    topicKey,
    recommendation,
    sourceMessageId: sourceMessageId ?? null,
    summary: `Recommendation [${topicKey}] · ${recommendation}`,
    actorUid: actor.uid,
    actorName: actor.name,
    at: serverTimestamp(),
  });
}

/**
 * Recommendation-only ledger listener for the auto-recall surface.
 * Reuses the same immutable `ledger` collection (ordered newest-first by
 * the existing `listenLedger`); this filters client-side to
 * recommendation events and shapes them as RecommendationEvent.
 */
export function listenRecommendations(
  pid: string,
  cb: (recs: RecommendationEvent[]) => void,
  onErr?: (e: Error) => void,
): Unsubscribe {
  return listenLedger(
    pid,
    (entries) => {
      cb(
        entries
          .filter((e) => e.eventType === 'recommendation' && e.topicKey)
          .map((e) => ({
            id: e.id,
            eventType: 'recommendation' as const,
            topicKey: e.topicKey as string,
            recommendation: e.recommendation ?? e.summary,
            sourceMessageId: e.sourceMessageId,
            summary: e.summary,
            actorUid: e.actorUid,
            actorName: e.actorName,
            at: e.at,
          })),
      );
    },
    onErr,
  );
}
